import { useEffect, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Progress } from '@/components/ui/progress';

export interface NonBlockingProgressStripProps {
  /**
   * `'inline'`  — used inside the chat thread (D.6 substep, D.7 in-chat regen).
   * `'banner'`  — used as a sticky top strip in the Results tab (D.8 full-pipeline rerun).
   */
  variant: 'inline' | 'banner';
  /** Primary phase / operation label */
  title: string;
  /** Optional indented substep label (smaller font, indented under title) */
  substep?: string;
  /** Current unit of work — renders a progress bar when present alongside `total` */
  current?: number;
  /** Total units of work — required when `current` is present */
  total?: number;
  /**
   * ISO 8601 timestamp when this operation started.
   * When provided, an elapsed-time counter ("47s") is rendered.
   * When absent, no timer is shown.
   */
  startedAt?: string;
  /** Cancel / abort callback. When absent, no cancel affordance is rendered. */
  onCancel?: () => void;
  /** Label for an optional primary action button (e.g. "Refresh now") */
  actionLabel?: string;
  /** Callback for the optional primary action button */
  onAction?: () => void;
}

// ─── Elapsed timer sub-component ──────────────────────────────────────────

function useElapsed(startedAt?: string): string | null {
  const [elapsed, setElapsed] = useState<number | null>(null);

  useEffect(() => {
    if (!startedAt) return undefined;
    const start = new Date(startedAt).getTime();
    if (Number.isNaN(start)) return undefined;

    const tick = () => {
      const seconds = Math.max(0, Math.floor((Date.now() - start) / 1000));
      setElapsed(seconds);
    };
    tick(); // immediate first paint
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startedAt]);

  if (elapsed === null) return null;
  if (elapsed < 60) return `${elapsed}s`;
  const m = Math.floor(elapsed / 60);
  const s = elapsed % 60;
  return `${m}m ${s}s`;
}

// ─── Component ─────────────────────────────────────────────────────────────

/**
 * Non-blocking progress strip for long-running operations.
 *
 * Two visual variants share the same prop surface:
 *  - `inline`  — compact, appears inside the chat thread or beneath a tool-status row
 *  - `banner`  — full-width sticky strip at the top of the Results content area
 *
 * Used by:
 *  - D.6: per-tool substep progress (inline, beneath each ToolProgressStepper row)
 *  - D.7: in-chat comparison regen progress (inline, inside ChatThread)
 *  - D.8: full-pipeline analysis rerun (banner, at top of ProjectResults)
 */
function NonBlockingProgressStrip({
  variant,
  title,
  substep,
  current,
  total,
  startedAt,
  onCancel,
  actionLabel,
  onAction,
}: NonBlockingProgressStripProps) {
  const elapsedLabel = useElapsed(startedAt);
  const showProgress = current !== undefined && total !== undefined && total > 0;
  const progressPercent = showProgress ? Math.round((current! / total!) * 100) : 0;

  if (variant === 'banner') {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-label={title}
        className="w-full border-b border-amber-200 bg-amber-50 px-5 py-2.5"
      >
        <div className="flex items-center gap-3 max-w-4xl mx-auto">
          <Loader2 className="h-4 w-4 animate-spin text-amber-600 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-amber-800">{title}</span>
              {elapsedLabel && (
                <span
                  className="text-xs text-amber-600 tabular-nums"
                  aria-label={`Elapsed: ${elapsedLabel}`}
                >
                  {elapsedLabel}
                </span>
              )}
              {showProgress && (
                <span className="text-xs text-amber-600 tabular-nums">
                  {current}/{total}
                </span>
              )}
            </div>
            {substep && (
              <p className="text-xs text-amber-700 mt-0.5 pl-0 leading-snug">{substep}</p>
            )}
            {showProgress && (
              <Progress
                value={progressPercent}
                className="mt-1.5 h-1 bg-amber-200"
                aria-label={`Progress: ${progressPercent}%`}
              />
            )}
          </div>
          {actionLabel && onAction && (
            <button
              type="button"
              onClick={onAction}
              className="shrink-0 text-xs font-semibold text-amber-800 underline hover:text-amber-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
            >
              {actionLabel}
            </button>
          )}
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              aria-label="Cancel"
              className="shrink-0 p-1 rounded text-amber-600 hover:text-amber-800 hover:bg-amber-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
    );
  }

  // variant === 'inline'
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={title}
      className="flex items-start gap-1.5 pl-3 py-1"
    >
      <Loader2 className="h-3 w-3 animate-spin text-primary/60 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-xs font-medium text-neutral-600">{title}</span>
          {elapsedLabel && (
            <span
              className="text-[10px] text-neutral-400 tabular-nums"
              aria-label={`Elapsed: ${elapsedLabel}`}
            >
              {elapsedLabel}
            </span>
          )}
          {showProgress && (
            <span className="text-[10px] text-neutral-400 tabular-nums">
              {current}/{total}
            </span>
          )}
          {actionLabel && onAction && (
            <button
              type="button"
              onClick={onAction}
              className={cn(
                'text-[10px] font-semibold text-primary underline hover:text-primary/80',
                'focus:outline-none focus-visible:ring-1 focus-visible:ring-primary/30 rounded',
              )}
            >
              {actionLabel}
            </button>
          )}
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              aria-label="Cancel"
              className="text-[10px] text-neutral-400 hover:text-neutral-600 underline focus:outline-none"
            >
              cancel
            </button>
          )}
        </div>
        {substep && (
          <p className="text-[10px] text-neutral-400 mt-0.5 pl-0 leading-snug italic">{substep}</p>
        )}
        {showProgress && (
          <Progress
            value={progressPercent}
            className="mt-1 h-0.5 bg-neutral-100"
            aria-label={`Progress: ${progressPercent}%`}
          />
        )}
      </div>
    </div>
  );
}

export default NonBlockingProgressStrip;
